import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { TRANSLATIONS, LANGUAGES, DEFAULT_LOCALE } from "./translations";

const STORAGE_KEY = "hbs_locale";

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(DEFAULT_LOCALE);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage on mount; default to "ht" if absent
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const valid = LANGUAGES.find((l) => l.code === saved);
      if (valid) {
        setLocaleState(valid.code);
      } else {
        setLocaleState(DEFAULT_LOCALE);
        localStorage.setItem(STORAGE_KEY, DEFAULT_LOCALE);
      }
    } catch {
      setLocaleState(DEFAULT_LOCALE);
    } finally {
      setHydrated(true);
    }
  }, []);

  const setLocale = useCallback((code) => {
    const valid = LANGUAGES.find((l) => l.code === code);
    if (!valid) return;
    setLocaleState(valid.code);
    try {
      localStorage.setItem(STORAGE_KEY, valid.code);
    } catch {
      /* noop */
    }
  }, []);

  const t = useCallback(
    (key) => {
      const dict = TRANSLATIONS[locale] || TRANSLATIONS[DEFAULT_LOCALE];
      return dict[key] ?? TRANSLATIONS.en?.[key] ?? key;
    },
    [locale]
  );

  // Translate shipment status, with method-aware override for "In Transit"
  const tStatus = useCallback(
    (status, method) => {
      if (!status) return "";
      const composite = method ? `status.${status}.${method}` : null;
      const dict = TRANSLATIONS[locale] || TRANSLATIONS[DEFAULT_LOCALE];
      if (composite && dict[composite]) return dict[composite];
      return dict[`status.${status}`] ?? TRANSLATIONS.en?.[`status.${status}`] ?? status;
    },
    [locale]
  );

  // Update <html lang> + document title on locale change
  useEffect(() => {
    if (!hydrated) return;
    try {
      document.documentElement.lang = locale;
      const dict = TRANSLATIONS[locale] || TRANSLATIONS[DEFAULT_LOCALE];
      if (dict["page.title"]) document.title = dict["page.title"];
    } catch {
      /* noop */
    }
  }, [locale, hydrated]);

  const value = useMemo(
    () => ({ locale, setLocale, t, tStatus, hydrated, languages: LANGUAGES }),
    [locale, setLocale, t, tStatus, hydrated]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useT must be used inside <LanguageProvider>");
  return ctx;
};
